import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../images/Inovaxe_logo-FINAL-3x11-45373564.png';
import '../Styles/Login.css';
import { loginUser } from '../Services/authService';
import config, { loadConfig } from '../Config';
import MaskedPasswordInput from '../components/MaskedPasswordInput';

const SAVED_USERS_KEY = 'savedLoginUsers';
const MAX_SAVED_USERS = 8;

const encodeSecret = (value) => {
  try {
    return btoa(unescape(encodeURIComponent(value || '')));
  } catch {
    return value || '';
  }
};

const decodeSecret = (value) => {
  if (!value) return '';
  try {
    return decodeURIComponent(escape(atob(value)));
  } catch {
    return value;
  }
};

const readSavedUsers = () => {
  try {
    const raw = localStorage.getItem(SAVED_USERS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeSavedUsers = (users) => {
  localStorage.setItem(SAVED_USERS_KEY, JSON.stringify(users.slice(0, MAX_SAVED_USERS)));
};

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [savedUsers, setSavedUsers] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [error, setError] = useState('');
  const emailWrapRef = useRef(null);
  const autofillPasswordRef = useRef(null);
  const savedUsersRef = useRef([]);
  const navigate = useNavigate();

  useEffect(() => {
    savedUsersRef.current = savedUsers;
  }, [savedUsers]);

  const passwordForEmail = (nextEmail) => {
    const match = savedUsersRef.current.find(
      (user) => (user.email || '').toLowerCase() === (nextEmail || '').trim().toLowerCase()
    );
    return match ? decodeSecret(match.password) : '';
  };

  const applySavedUser = (user) => {
    const nextEmail = user?.email || '';
    const nextPassword = decodeSecret(user?.password);
    setEmail(nextEmail);
    setPassword(nextPassword);
    setShowSaved(false);
    setError('');
  };

  useEffect(() => {
    const users = readSavedUsers();
    savedUsersRef.current = users;
    setSavedUsers(users);
    const lastUser = users[0];
    if (lastUser?.email) {
      applySavedUser(lastUser);
    }
  }, []);

  useEffect(() => {
    const onPointerDown = (event) => {
      if (!emailWrapRef.current?.contains(event.target)) {
        setShowSaved(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const matchingUsers = useMemo(() => {
    const q = email.trim().toLowerCase();
    if (!q) return savedUsers;
    return savedUsers.filter((user) => (user.email || '').toLowerCase().includes(q));
  }, [email, savedUsers]);

  const forgetSavedUser = (event, userEmail) => {
    event.preventDefault();
    event.stopPropagation();
    const next = savedUsers.filter((user) => user.email !== userEmail);
    setSavedUsers(next);
    writeSavedUsers(next);
    if (email === userEmail) {
      setEmail('');
      setPassword('');
    }
  };

  const handleEmailChange = (value) => {
    setEmail(value);
    const savedPassword = passwordForEmail(value);
    if (savedPassword) {
      setPassword(savedPassword);
      return;
    }
    const copyAutofill = () => {
      const filled = autofillPasswordRef.current?.value;
      if (filled) setPassword(filled);
    };
    requestAnimationFrame(copyAutofill);
    setTimeout(copyAutofill, 80);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    try {
      await loadConfig();
      const { accessToken, refreshToken, agentId, userName, userRole, menuPermissions } = await loginUser(email, password);

      localStorage.setItem('auth', accessToken);
      if (accessToken) localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('agentId', agentId);
      localStorage.setItem('name', userName);
      localStorage.setItem('userRole', userRole);
      try {
        localStorage.setItem('menuPermissions', JSON.stringify(menuPermissions || {}));
      } catch (persistErr) {
        console.warn('Failed to persist menuPermissions', persistErr);
      }

      const nextSaved = [
        { email: email.trim(), password: encodeSecret(password), lastUsed: Date.now() },
        ...savedUsers.filter((user) => (user.email || '').toLowerCase() !== email.trim().toLowerCase()),
      ];
      writeSavedUsers(nextSaved);

      const name = email.split('@')[0];
      localStorage.setItem('username', name.charAt(0).toUpperCase() + name.slice(1));
      setPassword('');

      navigate('/Dashboard');
    } catch (err) {
      setError(err.message || 'Invalid email or password');
    }
  };

  const isTestEnv = !!config.isTest;

  return (
    <div className={`login-container${isTestEnv ? " is-test" : ""}`}>
      <div className="login-bg" aria-hidden="true">
        <span className="login-orb login-orb-1" />
        <span className="login-orb login-orb-2" />
        <span className="login-orb login-orb-3" />
        <span className="login-diamond login-diamond-1" />
        <span className="login-diamond login-diamond-2" />
        <span className="login-diamond login-diamond-3" />
      </div>
      <div className="login-box">
        <div className="login-logo-wrap">
          <img src={logo} alt="Inovaxe Logo" className="login-logo" />
        </div>
        <p className="login-kicker">
          Inovaxe Quoting Tool
          {isTestEnv && <span className="test-env-badge">TEST</span>}
        </p>
        <h2 className="login-title">Welcome back</h2>
        <p className="login-subtitle">Sign in to create and manage quotes</p>

        <form onSubmit={handleLogin} autoComplete="on">
          <div className="form-group" ref={emailWrapRef}>
            <label htmlFor="login-email">Email:</label>
            <input
              id="login-email"
              type="email"
              name="username"
              autoComplete="username"
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              onInput={(e) => handleEmailChange(e.target.value)}
              onFocus={() => setShowSaved(true)}
              placeholder="you@example.com"
              required
            />
            {showSaved && matchingUsers.length > 0 && (
              <ul className="login-saved-list" role="listbox">
                {matchingUsers.map((user) => (
                  <li key={user.email}>
                    <button
                      type="button"
                      className="login-saved-item"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        applySavedUser(user);
                      }}
                    >
                      <span>{user.email}</span>
                      <small>Saved account</small>
                    </button>
                    <button
                      type="button"
                      className="login-saved-forget"
                      aria-label={`Remove ${user.email}`}
                      onMouseDown={(event) => forgetSavedUser(event, user.email)}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="login-password">Password:</label>
            <input
              ref={autofillPasswordRef}
              className="login-autofill-password"
              type="password"
              name="password"
              autoComplete="current-password"
              tabIndex={-1}
              aria-hidden="true"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onInput={(e) => setPassword(e.target.value)}
            />
            <MaskedPasswordInput
              id="login-password"
              className="login-password"
              required
              value={password}
              onPasswordChange={setPassword}
            />
          </div>

          {error && <p className="error">{error}</p>}

          <button type="submit" className="login-button">Login</button>

          <p className="forgot-text">
            Forgot your password? <a href="#">Reset here</a>
          </p>
        </form>
      </div>
    </div>
  );
}

export default Login;
